import { z } from "zod";

/**
 * Sunucudaki `requestPasswordResetSchema` ve `completePasswordResetSchema` ile birebir.
 *
 * Parola kuralı `zPassword`'ün kopyası — uzunluk sınırları OpenAPI belgesinde var ama iki
 * farklı karakter türü kuralı yalnız şemanın kendisinde, o yüzden oradan okunup buraya
 * yazılıyor. `AcceptInvite.schema.ts` ile aynı gerekçe ve aynı kurallar: API'nin
 * uygulamadığı bir kural bu formun tutamayacağı bir söz, uyguladığı ama burada olmayan bir
 * kural ise boşa giden bir tur.
 *
 * Jeton sınırları da sunucunun: 20–200. Postadaki bağlantı `?token=` ile geliyor.
 */
const PASSWORD_CLASSES: readonly RegExp[] = [/[a-zçğıöşü]/, /[A-ZÇĞİÖŞÜ]/, /\d/, /[^\p{L}\p{N}]/u];

/** Giriş formunun cümleleriyle aynı (`Login.schema.ts`): aynı alan, iki ekranda iki cümle olmasın. */
export const requestPasswordResetSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "E-posta adresinizi yazın.")
    .email("Geçerli bir e-posta adresi yazın (ör. ad@sirket.com)."),
});

/**
 * Yeni bir parolanın kuralı — sunucunun `zPassword`'ü. Sıfırlama bağlantısı, ilk girişte zorunlu değiştirme
 * ve yöneticinin verdiği parola (31 Ekim 2026) aynı kuraldan geçiyor: üç ekranda üç kopya olmasın.
 */
export const newPasswordSchema = z
  .string()
  .min(12, "Parola en az 12 karakter olmalı")
  .max(200, "Parola en fazla 200 karakter olabilir")
  .refine(
    (value) => PASSWORD_CLASSES.filter((rule) => rule.test(value)).length >= 2,
    "Parola en az iki farklı karakter türü içermeli (küçük harf, büyük harf, rakam, sembol)",
  );

export const completePasswordResetSchema = z.object({
  token: z
    .string()
    .trim()
    .min(20, "Sıfırlama kodu eksik veya bozuk")
    .max(200, "Sıfırlama kodu eksik veya bozuk"),
  password: newPasswordSchema,
});

/**
 * Postadaki bağlantının jetonu şemadan geçiyor mu — geçmiyorsa bağlantı daha formdan ÖNCE ölü.
 *
 * Kesilmiş ya da elle bozulmuş bir bağlantı (9 Ekim 2026, 5. tur) kişiye yeni parolasını yazdırıp
 * ancak «Parolayı kaydet»ten sonra bir «Sıfırlama kodu» alanı açıyordu — kişinin elinde hiç
 * «kod» olmamıştı, yalnız bir bağlantı. Davetten farkı: davet kodu yönetici tarafından elle
 * iletilebiliyor (`InviteUserDrawer`), sıfırlama jetonu yalnız postayla gidiyor.
 */
export function isUsableResetToken(token: string): boolean {
  return completePasswordResetSchema.shape.token.safeParse(token).success;
}

export type RequestPasswordResetFormValues = z.infer<typeof requestPasswordResetSchema>;
export type CompletePasswordResetFormValues = z.infer<typeof completePasswordResetSchema>;
